import { buildApiUrl, appConfig } from "./config";
import { currentLegalVersions } from "./legal/legalDocuments";

export function isApiConfigured() {
  if (process.env.NODE_ENV === "development") return true;
  return Boolean(String(appConfig.API_BASE_URL || "").trim());
}

async function readApiBody(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * POST /auth/signin — returns { user, token } when ok: true.
 */
/**
 * POST /auth/verify-turnstile — verify Cloudflare Turnstile token (signup / resend gates).
 */
export async function postVerifyTurnstileToken(token) {
  const res = await fetch(buildApiUrl("/auth/verify-turnstile"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token: String(token || "") }),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Verification failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok || !body?.data?.verified) {
    throw new Error("Unexpected response from server");
  }
  return body.data;
}

/**
 * POST /auth/signin — returns { user, token } when ok: true.
 */
/**
 * POST /auth/signup — persists user when API has DATABASE_URL; otherwise mock.
 * Returns `{ user, token }` (token may be present for server session; verify-email flow can ignore it).
 */
export async function postSignup({
  email,
  password,
  fullName,
  signupType,
  countryCode,
  mobile,
  companyName,
  companyWebsite,
  acceptTerms,
  termsVersion,
  privacyVersion,
} = {}) {
  const versions = currentLegalVersions();
  const res = await fetch(buildApiUrl("/auth/signup"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email: String(email || "").trim(),
      password: String(password || ""),
      fullName: String(fullName || "").trim(),
      signupType: String(signupType || "applicant"),
      countryCode: String(countryCode || "").trim(),
      mobile: String(mobile || "").trim(),
      companyName: companyName != null ? String(companyName) : "",
      companyWebsite: companyWebsite != null ? String(companyWebsite) : "",
      acceptTerms: acceptTerms === true,
      termsVersion: String(termsVersion || versions.terms || ""),
      privacyVersion: String(privacyVersion || versions.privacy || ""),
    }),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Sign up failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok || !body?.data?.user || !body?.data?.token) {
    throw new Error("Unexpected response from server");
  }
  return {
    user: body.data.user,
    token: body.data.token,
    emailVerificationSent: body.data.emailVerificationSent === true,
    emailVerificationError: body.data.emailVerificationError,
  };
}

/**
 * POST /auth/verify-email — confirms 6-digit code; optional Bearer session refresh.
 */
export async function postVerifyEmail({ email, code, turnstileToken, authToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  const t = String(authToken || "").trim();
  if (t) headers.Authorization = `Bearer ${t}`;
  const payload = {
    email: String(email || "").trim().toLowerCase(),
    code: String(code || "").trim(),
  };
  const ts = String(turnstileToken || "").trim();
  if (ts) payload.turnstileToken = ts;
  const res = await fetch(buildApiUrl("/auth/verify-email"), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Verification failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * GET /auth/verification-email-status?email= — send count / exhausted before contact support.
 */
export async function getVerificationEmailStatus(email) {
  const e = String(email || "").trim();
  if (!e) return null;
  const res = await fetch(
    `${buildApiUrl("/auth/verification-email-status")}?${new URLSearchParams({ email: e })}`
  );
  const body = await readApiBody(res);
  if (!res.ok || !body?.ok || !body?.data) return null;
  return body.data;
}

/**
 * POST /auth/resend-verification-email — new code + email (rate limited).
 */
export async function postResendVerificationEmail({ email, turnstileToken, authToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  const t = String(authToken || "").trim();
  if (t) headers.Authorization = `Bearer ${t}`;
  const payload = {
    email: String(email || "").trim().toLowerCase(),
  };
  const ts = String(turnstileToken || "").trim();
  if (ts) payload.turnstileToken = ts;
  const res = await fetch(buildApiUrl("/auth/resend-verification-email"), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok) {
    throw new Error("Unexpected response from server");
  }
  return body.data || {};
}

/**
 * GET /auth/phone-verification/lookup — Twilio Lookup (Flow 2); Bearer session.
 */
export async function getPhoneVerificationLookup(authToken) {
  const headers = {};
  const t = String(authToken || "").trim();
  if (!t) return null;
  headers.Authorization = `Bearer ${t}`;
  const res = await fetch(buildApiUrl("/auth/phone-verification/lookup"), { headers });
  const body = await readApiBody(res);
  if (!res.ok || !body?.ok || body?.data == null) return null;
  return body.data;
}

/**
 * GET /auth/verification-phone-status — send count / exhausted (Bearer session).
 */
export async function getVerificationPhoneStatus(authToken) {
  const headers = {};
  const t = String(authToken || "").trim();
  if (!t) return null;
  headers.Authorization = `Bearer ${t}`;
  const res = await fetch(buildApiUrl("/auth/verification-phone-status"), { headers });
  const body = await readApiBody(res);
  if (!res.ok || !body?.ok || !body?.data) return null;
  return body.data;
}

/**
 * POST /auth/resend-phone-verification — new SMS code (rate limited, Turnstile).
 */
export async function postResendPhoneVerification({ turnstileToken, authToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  const t = String(authToken || "").trim();
  if (t) headers.Authorization = `Bearer ${t}`;
  const payload = {};
  const ts = String(turnstileToken || "").trim();
  if (ts) payload.turnstileToken = ts;
  const res = await fetch(buildApiUrl("/auth/resend-phone-verification"), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok) {
    throw new Error("Unexpected response from server");
  }
  return body.data || {};
}

/**
 * POST /auth/verify-phone — confirm 6-digit SMS code (Turnstile).
 */
export async function postVerifyPhone({ code, turnstileToken, authToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  const tok = String(authToken || "").trim();
  if (tok) headers.Authorization = `Bearer ${tok}`;
  const payload = {
    code: String(code || "").trim(),
  };
  const ts = String(turnstileToken || "").trim();
  if (ts) payload.turnstileToken = ts;
  const res = await fetch(buildApiUrl("/auth/verify-phone"), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Verification failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * PATCH /auth/account/mobile — update mobile when email is already verified (authenticated).
 */
export async function patchAuthAccountMobile({
  countryCode,
  mobile,
  turnstileToken,
  authToken,
} = {}) {
  const headers = { "Content-Type": "application/json" };
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in. Open sign up from the mobile verification page again.");
  }
  headers.Authorization = `Bearer ${t}`;
  const payload = {
    countryCode: String(countryCode || "").trim(),
    mobile: String(mobile || "").trim(),
  };
  const ts = String(turnstileToken || "").trim();
  if (ts) payload.turnstileToken = ts;
  const res = await fetch(buildApiUrl("/auth/account/mobile"), {
    method: "PATCH",
    headers,
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Update failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * GET /auth/me — current session user from DB when available (fresh recruitment defaults, profile fields).
 */
export async function getAuthMe(authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/auth/me"), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * POST /legal/accept — record that the signed-in user accepts the current legal documents.
 * When `documents` is omitted, the API records acceptance of every current document.
 */
export async function postAcceptLegal(authToken, documents) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/legal/accept"), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
    body: JSON.stringify(Array.isArray(documents) && documents.length ? { documents } : {}),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.legal) {
    throw new Error("Unexpected response from server");
  }
  return { legal: body.data.legal };
}

export async function postSignin({ email, password, signupType } = {}) {
  const res = await fetch(buildApiUrl("/auth/signin"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email: String(email || "").trim(),
      password: String(password || ""),
      ...(signupType ? { signupType } : {}),
    }),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Sign in failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user || !body?.data?.token) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user, token: body.data.token };
}

/** POST /auth/admin-signin — internal ops console only. */
export async function postAdminSignin({ email, password } = {}) {
  const res = await fetch(buildApiUrl("/auth/admin-signin"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email: String(email || "").trim(),
      password: String(password || ""),
    }),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Sign in failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user || !body?.data?.token) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user, token: body.data.token };
}

/** GET /admin/alerts */
export async function getAdminAlerts(authToken, { status = "open", limit = 50 } = {}) {
  const t = String(authToken || "").trim();
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (limit) params.set("limit", String(limit));
  const qs = params.toString();
  const res = await fetch(buildApiUrl(`/admin/alerts${qs ? `?${qs}` : ""}`), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** PATCH /admin/alerts/:id/complete */
export async function patchAdminAlertComplete(authToken, alertId, { note } = {}) {
  const t = String(authToken || "").trim();
  const res = await fetch(buildApiUrl(`/admin/alerts/${encodeURIComponent(alertId)}/complete`), {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ note: note != null ? String(note) : "" }),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** GET /admin/audit-events — append-only platform activity log */
export async function getAdminAuditEvents(authToken, filters = {}) {
  const t = String(authToken || "").trim();
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value != null && String(value).trim() !== "") {
      params.set(key, String(value).trim());
    }
  }
  const qs = params.toString();
  const res = await fetch(buildApiUrl(`/admin/audit-events${qs ? `?${qs}` : ""}`), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** GET /admin/reports/new-users — daily product sign-ups */
export async function getAdminReportsNewUsers(authToken, { days, from, to } = {}) {
  const t = String(authToken || "").trim();
  const params = new URLSearchParams();
  if (days != null && String(days).trim() !== "") params.set("days", String(days).trim());
  if (from) params.set("from", String(from).trim());
  if (to) params.set("to", String(to).trim());
  const qs = params.toString();
  const res = await fetch(buildApiUrl(`/admin/reports/new-users${qs ? `?${qs}` : ""}`), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** GET /admin/users — search product users by email */
export async function getAdminUsers(authToken, { email, signupType, limit = 50, offset = 0 } = {}) {
  const t = String(authToken || "").trim();
  const params = new URLSearchParams();
  if (email) params.set("email", String(email).trim());
  if (signupType) params.set("signupType", String(signupType).trim());
  if (limit != null) params.set("limit", String(limit));
  if (offset != null) params.set("offset", String(offset));
  const qs = params.toString();
  const res = await fetch(buildApiUrl(`/admin/users${qs ? `?${qs}` : ""}`), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** GET /admin/users/:userId — full product user detail */
export async function getAdminUser(authToken, userId) {
  const t = String(authToken || "").trim();
  const id = String(userId || "").trim();
  if (!id) throw new Error("User id is required.");
  const res = await fetch(buildApiUrl(`/admin/users/${encodeURIComponent(id)}`), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok || !body?.data?.user) throw new Error("Unexpected response from server");
  return body.data.user;
}

/** POST /admin/users/:userId/bonus-job-slots — grant prepaid job slots */
export async function addAdminUserBonusJobSlots(authToken, userId, slots) {
  const t = String(authToken || "").trim();
  const id = String(userId || "").trim();
  const count = Math.floor(Number(slots));
  if (!id) throw new Error("User id is required.");
  if (!Number.isFinite(count) || count <= 0) throw new Error("Enter at least 1 job slot to add.");
  const res = await fetch(buildApiUrl(`/admin/users/${encodeURIComponent(id)}/bonus-job-slots`), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ slots: count }),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** GET /admin/fx-rates */
export async function getAdminFxRates(authToken) {
  const t = String(authToken || "").trim();
  const res = await fetch(buildApiUrl("/admin/fx-rates"), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** PUT /admin/fx-rates */
export async function putAdminFxRates(authToken, rates) {
  const t = String(authToken || "").trim();
  const res = await fetch(buildApiUrl("/admin/fx-rates"), {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ rates }),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** GET /admin/pricing/job-prices */
export async function getAdminJobPrices(authToken) {
  const t = String(authToken || "").trim();
  const res = await fetch(buildApiUrl("/admin/pricing/job-prices"), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** PUT /admin/pricing/job-prices */
export async function putAdminJobPrices(authToken, prices) {
  const t = String(authToken || "").trim();
  const res = await fetch(buildApiUrl("/admin/pricing/job-prices"), {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ prices }),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** GET /admin/pricing/subscription-plans */
export async function getAdminSubscriptionPlans(authToken) {
  const t = String(authToken || "").trim();
  const res = await fetch(buildApiUrl("/admin/pricing/subscription-plans"), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** POST /admin/pricing/subscription-plans */
export async function postAdminSubscriptionPlan(authToken, payload) {
  const t = String(authToken || "").trim();
  const res = await fetch(buildApiUrl("/admin/pricing/subscription-plans"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** PATCH /admin/pricing/subscription-plans/:id */
export async function patchAdminSubscriptionPlan(authToken, planId, payload) {
  const t = String(authToken || "").trim();
  const res = await fetch(buildApiUrl(`/admin/pricing/subscription-plans/${planId}`), {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** DELETE /admin/pricing/subscription-plans/:id */
export async function deleteAdminSubscriptionPlan(authToken, planId) {
  const t = String(authToken || "").trim();
  const res = await fetch(buildApiUrl(`/admin/pricing/subscription-plans/${planId}`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** GET /admin/pricing/country-currencies */
export async function getAdminCountryCurrencies(authToken) {
  const t = String(authToken || "").trim();
  const res = await fetch(buildApiUrl("/admin/pricing/country-currencies"), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** PUT /admin/pricing/country-currencies */
export async function putAdminCountryCurrency(authToken, payload) {
  const t = String(authToken || "").trim();
  const res = await fetch(buildApiUrl("/admin/pricing/country-currencies"), {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/** DELETE /admin/pricing/country-currencies/:countryCode */
export async function deleteAdminCountryCurrency(authToken, countryCode) {
  const t = String(authToken || "").trim();
  const res = await fetch(buildApiUrl(`/admin/pricing/country-currencies/${countryCode}`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/**
 * POST /auth/forgot-password — always succeeds with generic message when DB is up (no email enumeration).
 */
export async function postForgotPassword({ email, turnstileToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  const payload = { email: String(email || "").trim().toLowerCase() };
  const ts = String(turnstileToken || "").trim();
  if (ts) payload.turnstileToken = ts;
  const res = await fetch(buildApiUrl("/auth/forgot-password"), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data) {
    throw new Error("Unexpected response from server");
  }
  return body.data;
}

/**
 * POST /auth/reset-password — set new password using token from email link.
 */
export async function postResetPassword({ token, password, turnstileToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  const payload = {
    token: String(token || "").trim(),
    password: String(password || ""),
  };
  const ts = String(turnstileToken || "").trim();
  if (ts) payload.turnstileToken = ts;
  const res = await fetch(buildApiUrl("/auth/reset-password"), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok) {
    throw new Error("Unexpected response from server");
  }
  return body.data || {};
}

/**
 * POST /auth/email-change/request — logged-in user; sends link to current email.
 */
export async function postRequestEmailChange(authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/auth/email-change/request"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    err.details = body?.error?.details;
    throw err;
  }
  if (!body?.ok || !body?.data) {
    throw new Error("Unexpected response from server");
  }
  return body.data;
}

/**
 * GET /auth/email-change/request-eligibility — rate limit / lock state for profile button.
 */
export async function getEmailChangeLinkRequestEligibility(authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/auth/email-change/request-eligibility"), {
    method: "GET",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok || body?.data == null) {
    throw new Error("Unexpected response from server");
  }
  return body.data;
}

/**
 * GET /auth/email-change/status?token=
 */
export async function getEmailChangeStatus(token) {
  const raw = String(token || "").trim();
  const res = await fetch(
    `${buildApiUrl("/auth/email-change/status")}?token=${encodeURIComponent(raw)}`
  );
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok || body?.data == null) {
    throw new Error("Unexpected response from server");
  }
  return body.data;
}

/**
 * POST /auth/email-change/new-email — token + new address; sends code to new email.
 */
export async function postEmailChangeNewEmail({ token, newEmail, turnstileToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  const payload = {
    token: String(token || "").trim(),
    newEmail: String(newEmail || "").trim().toLowerCase(),
  };
  const ts = String(turnstileToken || "").trim();
  if (ts) payload.turnstileToken = ts;
  const res = await fetch(buildApiUrl("/auth/email-change/new-email"), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok) {
    throw new Error("Unexpected response from server");
  }
  return body.data || {};
}

/**
 * POST /auth/email-change/resend
 */
export async function postEmailChangeResend({ token, turnstileToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  const payload = { token: String(token || "").trim() };
  const ts = String(turnstileToken || "").trim();
  if (ts) payload.turnstileToken = ts;
  const res = await fetch(buildApiUrl("/auth/email-change/resend"), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok) {
    throw new Error("Unexpected response from server");
  }
  return body.data || {};
}

/**
 * POST /auth/email-change/verify — completes change; optional Bearer refreshes session user.
 */
export async function postEmailChangeVerify({ token, code, turnstileToken, authToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  const at = String(authToken || "").trim();
  if (at) headers.Authorization = `Bearer ${at}`;
  const payload = {
    token: String(token || "").trim(),
    code: String(code || "").trim(),
  };
  const ts = String(turnstileToken || "").trim();
  if (ts) payload.turnstileToken = ts;
  const res = await fetch(buildApiUrl("/auth/email-change/verify"), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * POST /auth/mobile-change/request — logged-in user; sends link to current email.
 */
export async function postRequestMobileChange(authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/auth/mobile-change/request"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    err.details = body?.error?.details;
    throw err;
  }
  if (!body?.ok || !body?.data) {
    throw new Error("Unexpected response from server");
  }
  return body.data;
}

/**
 * GET /auth/mobile-change/request-eligibility — rate limit / lock state for profile button.
 */
export async function getMobileChangeLinkRequestEligibility(authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/auth/mobile-change/request-eligibility"), {
    method: "GET",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok || body?.data == null) {
    throw new Error("Unexpected response from server");
  }
  return body.data;
}

/**
 * GET /auth/mobile-change/status?token=
 */
export async function getMobileChangeStatus(token) {
  const raw = String(token || "").trim();
  const res = await fetch(
    `${buildApiUrl("/auth/mobile-change/status")}?token=${encodeURIComponent(raw)}`
  );
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok || body?.data == null) {
    throw new Error("Unexpected response from server");
  }
  return body.data;
}

/**
 * POST /auth/mobile-change/start-verify — token + new number; sends SMS (Twilio Verify).
 */
export async function postMobileChangeStartVerify({ token, countryCode, mobile, turnstileToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  const payload = {
    token: String(token || "").trim(),
    countryCode: String(countryCode || "").trim(),
    mobile: String(mobile || "").trim(),
  };
  const ts = String(turnstileToken || "").trim();
  if (ts) payload.turnstileToken = ts;
  const res = await fetch(buildApiUrl("/auth/mobile-change/start-verify"), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok) {
    throw new Error("Unexpected response from server");
  }
  return body.data || {};
}

/**
 * POST /auth/mobile-change/resend
 */
export async function postMobileChangeResend({ token, turnstileToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  const payload = { token: String(token || "").trim() };
  const ts = String(turnstileToken || "").trim();
  if (ts) payload.turnstileToken = ts;
  const res = await fetch(buildApiUrl("/auth/mobile-change/resend"), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok) {
    throw new Error("Unexpected response from server");
  }
  return body.data || {};
}

/**
 * POST /auth/mobile-change/verify — completes change; signs user out everywhere server-side.
 */
export async function postMobileChangeVerify({ token, code, turnstileToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  const payload = {
    token: String(token || "").trim(),
    code: String(code || "").trim(),
  };
  const ts = String(turnstileToken || "").trim();
  if (ts) payload.turnstileToken = ts;
  const res = await fetch(buildApiUrl("/auth/mobile-change/verify"), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * GET /health — lightweight connectivity check (no auth).
 */
export async function getHealth() {
  const res = await fetch(buildApiUrl("/health"));
  const body = await readApiBody(res);
  return { ok: res.ok && body?.ok === true, body };
}

/**
 * PATCH /applicants/me/profile — persist applicant fields on the server when the API uses Postgres:
 * `dateOfBirth`, `countryOfResidence` (ISO-3166 alpha-2), optional `townCityOfResidence`, `workCountries` (rows with `id`,
 * `countryCodes` ISO-2 array, optional `clearProof`), and optional `socialProfiles` (`linkedin`, `instagram`, `youtube`, `customs`).
 */
export async function patchApplicantProfile(payload, authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/applicants/me/profile"), {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload && typeof payload === "object" ? payload : {}),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || body?.data?.profile == null) {
    throw new Error("Unexpected response from server");
  }
  return { profile: body.data.profile, user: body.data.user };
}

/**
 * PATCH /hiring/me/social-profiles — persist hiring social profiles on the server.
 */
export async function patchHiringSocialProfiles(payload, authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/hiring/me/social-profiles"), {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload && typeof payload === "object" ? payload : {}),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * PATCH /hiring/me/recruitment-defaults — default who can apply (anyone vs countries), optional
 * `defaultAdvertisingCurrency`, `defaultOperatingCountry` (ISO-2 operating country for pricing), job location
 * defaults; stored on `users`. Deprecated aliases: `defaultAccountCurrency`, `countryOfResidence`. Send `null`
 * or `""` to clear nullable fields. Omit a field to leave it unchanged.
 */
export async function patchHiringRecruitmentDefaults(payload, authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/hiring/me/recruitment-defaults"), {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload && typeof payload === "object" ? payload : {}),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * POST /hiring/me/job-posts — create hiring draft/published job post.
 */
export async function postHiringJobPost(payload, authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/hiring/me/job-posts"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload && typeof payload === "object" ? payload : {}),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.jobPost) {
    throw new Error("Unexpected response from server");
  }
  return { jobPost: body.data.jobPost };
}

/**
 * PATCH /hiring/me/job-posts/:id — update hiring draft/published job post.
 */
export async function patchHiringJobPost(id, payload, authToken) {
  const t = String(authToken || "").trim();
  const rawId = String(id || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  if (!rawId) {
    throw new Error("Missing job post id.");
  }
  const res = await fetch(buildApiUrl(`/hiring/me/job-posts/${encodeURIComponent(rawId)}`), {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload && typeof payload === "object" ? payload : {}),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.jobPost) {
    throw new Error("Unexpected response from server");
  }
  return { jobPost: body.data.jobPost };
}

/**
 * PATCH /hiring/me/job-posts/:jobPostId/applications/:applicantUserId — set applicationStatus (new|rejected|shortlisted|parked).
 */
export async function patchHiringJobApplicationStatus(
  jobPostId,
  applicantUserId,
  applicationStatus,
  authToken,
  { reportAsSpam, rejectionReason, sendRejectionNotification } = {}
) {
  const t = String(authToken || "").trim();
  const jp = String(jobPostId || "").trim();
  const ap = String(applicantUserId || "").trim();
  if (!t) throw new Error("Not signed in.");
  if (!jp || !ap) throw new Error("Missing job post id or applicant id.");
  const payload = { applicationStatus };
  if (reportAsSpam === true) payload.reportAsSpam = true;
  if (rejectionReason != null && String(rejectionReason).trim()) {
    payload.rejectionReason = String(rejectionReason).trim();
  }
  if (sendRejectionNotification === true) payload.sendRejectionNotification = true;
  else if (sendRejectionNotification === false) payload.sendRejectionNotification = false;
  const res = await fetch(
    buildApiUrl(`/hiring/me/job-posts/${encodeURIComponent(jp)}/applications/${encodeURIComponent(ap)}`),
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${t}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.application) {
    throw new Error("Unexpected response from server");
  }
  return {
    application: body.data.application,
    applicantUserId: body.data.applicantUserId,
    jobPostId: body.data.jobPostId,
  };
}

/**
 * POST /hiring/me/job-posts/:jobPostId/applications/:applicantUserId/report — report applicant and reject application.
 */
export async function reportHiringJobApplicant(jobPostId, applicantUserId, reportExplanation, authToken) {
  const t = String(authToken || "").trim();
  const jp = String(jobPostId || "").trim();
  const aid = String(applicantUserId || "").trim();
  const explanation = String(reportExplanation || "").trim();
  if (!t) throw new Error("Not signed in.");
  if (!jp || !aid) throw new Error("Missing job post id or applicant id.");
  if (!explanation) throw new Error("Please tell us what happened.");
  const res = await fetch(
    buildApiUrl(
      `/hiring/me/job-posts/${encodeURIComponent(jp)}/applications/${encodeURIComponent(aid)}/report`
    ),
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${t}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ reportExplanation: explanation }),
    }
  );
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.application) {
    throw new Error("Unexpected response from server");
  }
  return { application: body.data.application, report: body.data.report };
}

/**
 * GET /hiring/me/job-posts/:jobPostId/applications — applicants for this job (profile + pipeline status).
 */
export async function listHiringJobPostApplications(jobPostId, authToken) {
  const t = String(authToken || "").trim();
  const jp = String(jobPostId || "").trim();
  if (!t) throw new Error("Not signed in.");
  if (!jp) throw new Error("Missing job post id.");
  const res = await fetch(buildApiUrl(`/hiring/me/job-posts/${encodeURIComponent(jp)}/applications`), {
    headers: {
      Authorization: `Bearer ${t}`,
    },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !Array.isArray(body?.data?.applicants)) {
    throw new Error("Unexpected response from server");
  }
  return { applicants: body.data.applicants };
}

/**
 * POST /hiring/me/job-posts/:jobPostId/applicant-invites — email invites to apply on JOBUWIN.
 */
export async function postHiringApplicantInvites(authToken, jobPostId, payload) {
  const t = String(authToken || "").trim();
  const jp = String(jobPostId || "").trim();
  if (!t) throw new Error("Not signed in.");
  if (!jp) throw new Error("Missing job post id.");
  const res = await fetch(buildApiUrl(`/hiring/me/job-posts/${encodeURIComponent(jp)}/applicant-invites`), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload && typeof payload === "object" ? payload : {}),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data) {
    throw new Error("Unexpected response from server");
  }
  if (res.status === 202) {
    return {
      async: true,
      batchId: String(body.data.batchId || ""),
      queued: Number(body.data.queued) || 0,
      skipped: Number(body.data.skipped) || 0,
      message: String(body.data.message || "").trim(),
    };
  }
  return {
    sent: Number(body.data.sent) || 0,
    skipped: Number(body.data.skipped) || 0,
    failed: Number(body.data.failed) || 0,
    errors: Array.isArray(body.data.errors) ? body.data.errors : undefined,
  };
}

/**
 * GET /hiring/me/job-posts/:jobPostId/applicant-invites — list invites for this job.
 */
export async function listHiringApplicantInvites(authToken, jobPostId) {
  const t = String(authToken || "").trim();
  const jp = String(jobPostId || "").trim();
  if (!t) throw new Error("Not signed in.");
  if (!jp) throw new Error("Missing job post id.");
  const res = await fetch(buildApiUrl(`/hiring/me/job-posts/${encodeURIComponent(jp)}/applicant-invites`), {
    headers: {
      Authorization: `Bearer ${t}`,
    },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !Array.isArray(body?.data?.invites)) {
    throw new Error("Unexpected response from server");
  }
  return {
    invites: body.data.invites,
    summary: body.data.summary || null,
  };
}

/**
 * GET /hiring/me/job-posts/:jobPostId/applications/:applicantUserId/cv — download applicant CV.
 */
export async function downloadHiringApplicantCv(jobPostId, applicantUserId, authToken) {
  const t = String(authToken || "").trim();
  const jp = String(jobPostId || "").trim();
  const aid = String(applicantUserId || "").trim();
  if (!t) throw new Error("Not signed in.");
  if (!jp) throw new Error("Missing job post id.");
  if (!aid) throw new Error("Missing applicant id.");
  const res = await fetch(
    buildApiUrl(
      `/hiring/me/job-posts/${encodeURIComponent(jp)}/applications/${encodeURIComponent(aid)}/cv`
    ),
    {
      headers: {
        Authorization: `Bearer ${t}`,
      },
    }
  );
  if (!res.ok) {
    const body = await readApiBody(res);
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  const blob = await res.blob();
  const disposition = String(res.headers.get("Content-Disposition") || "");
  const fileNameMatch = disposition.match(/filename="([^"]+)"/i);
  const fileName = fileNameMatch?.[1] || "Applicant-CV.pdf";
  return { blob, fileName };
}

/**
 * POST /hiring/me/job-posts/:id/pitch-video — multipart field `pitch` (WebM or MP4).
 * Returns durable `pitchVideoUrl` for `<video src>` and job payloads.
 */
export async function postHiringJobPitchVideo(jobPostId, fileBlob, mimeType, authToken) {
  const t = String(authToken || "").trim();
  const rawId = String(jobPostId || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  if (!rawId) {
    throw new Error("Missing job post id.");
  }
  if (!(fileBlob instanceof Blob) || fileBlob.size === 0) {
    throw new Error("Pitch recording is empty or missing; record again before saving.");
  }
  const mimeRaw = String(mimeType || "video/webm").toLowerCase();
  const baseMime = mimeRaw.split(";")[0].trim() || "video/webm";
  let ext = "webm";
  if (baseMime === "video/mp4" || baseMime.includes("mp4")) ext = "mp4";
  else if (baseMime === "video/quicktime") ext = "mov";
  const file =
    fileBlob instanceof File
      ? fileBlob
      : new File([fileBlob], `pitch.${ext}`, { type: baseMime });
  const form = new FormData();
  form.append("pitch", file);
  const res = await fetch(buildApiUrl(`/hiring/me/job-posts/${encodeURIComponent(rawId)}/pitch-video`), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${t}`,
    },
    body: form,
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.pitchVideoUrl) {
    throw new Error("Unexpected response from server");
  }
  return { pitchVideoUrl: body.data.pitchVideoUrl, jobPost: body.data.jobPost };
}

/**
 * POST /applicants/me/job-posts/:jobPostId/application-pitch-video — multipart field `pitch` (WebM or MP4).
 * Persists the applicant's application pitch for a published job (upsert per applicant + job).
 */
export async function postApplicantApplicationPitchVideo(jobPostId, fileBlob, mimeType, authToken) {
  const t = String(authToken || "").trim();
  const rawId = String(jobPostId || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  if (!rawId) {
    throw new Error("Missing job post id.");
  }
  if (!(fileBlob instanceof Blob) || fileBlob.size === 0) {
    throw new Error("Pitch recording is empty or missing; record again before submitting.");
  }
  const mimeRaw = String(mimeType || "video/webm").toLowerCase();
  const baseMime = mimeRaw.split(";")[0].trim() || "video/webm";
  let ext = "webm";
  if (baseMime === "video/mp4" || baseMime.includes("mp4")) ext = "mp4";
  else if (baseMime === "video/quicktime") ext = "mov";
  const file =
    fileBlob instanceof File
      ? fileBlob
      : new File([fileBlob], `application-pitch.${ext}`, { type: baseMime });
  const form = new FormData();
  form.append("pitch", file);
  const res = await fetch(
    buildApiUrl(`/applicants/me/job-posts/${encodeURIComponent(rawId)}/application-pitch-video`),
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${t}`,
      },
      body: form,
    }
  );
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.pitchVideoUrl) {
    throw new Error("Unexpected response from server");
  }
  return { pitchVideoUrl: body.data.pitchVideoUrl, application: body.data.application };
}

/**
 * GET /applicants/me/job-posts/:jobPostId/my-application — returns `{ application }` or `application: null`.
 */
export async function getApplicantMyApplicationForJobPost(jobPostId, authToken) {
  const t = String(authToken || "").trim();
  const rawId = String(jobPostId || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  if (!rawId) {
    throw new Error("Missing job post id.");
  }
  const res = await fetch(buildApiUrl(`/applicants/me/job-posts/${encodeURIComponent(rawId)}/my-application`), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${t}`,
    },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok) {
    throw new Error("Unexpected response from server");
  }
  return { application: body.data?.application ?? null };
}

/**
 * POST /applicants/me/job-posts/interaction-status — batch flags for current applicant.
 * @returns {Promise<Record<string, { notForMe: boolean, saved: boolean, applied: boolean, appliedAt: string | null, savedAt: string | null, notForMeAt: string | null }>>}
 */
export async function postApplicantJobPostInteractionStatus(jobPostIds, authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const ids = Array.isArray(jobPostIds) ? jobPostIds.map((id) => String(id || "").trim()).filter(Boolean) : [];
  const res = await fetch(buildApiUrl("/applicants/me/job-posts/interaction-status"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ jobPostIds: ids }),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok) {
    throw new Error("Unexpected response from server");
  }
  return body.data?.byJobPostId && typeof body.data.byJobPostId === "object" ? body.data.byJobPostId : {};
}

/** PUT /applicants/me/job-posts/:jobPostId/not-for-me */
export async function putApplicantJobPostNotForMe(jobPostId, authToken) {
  const t = String(authToken || "").trim();
  const rawId = String(jobPostId || "").trim();
  if (!t) throw new Error("Not signed in.");
  if (!rawId) throw new Error("Missing job post id.");
  const res = await fetch(buildApiUrl(`/applicants/me/job-posts/${encodeURIComponent(rawId)}/not-for-me`), {
    method: "PUT",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
}

/** DELETE /applicants/me/job-posts/:jobPostId/not-for-me */
export async function deleteApplicantJobPostNotForMe(jobPostId, authToken) {
  const t = String(authToken || "").trim();
  const rawId = String(jobPostId || "").trim();
  if (!t) throw new Error("Not signed in.");
  if (!rawId) throw new Error("Missing job post id.");
  const res = await fetch(buildApiUrl(`/applicants/me/job-posts/${encodeURIComponent(rawId)}/not-for-me`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
}

/** PUT /applicants/me/job-posts/:jobPostId/saved */
export async function putApplicantJobPostSaved(jobPostId, authToken) {
  const t = String(authToken || "").trim();
  const rawId = String(jobPostId || "").trim();
  if (!t) throw new Error("Not signed in.");
  if (!rawId) throw new Error("Missing job post id.");
  const res = await fetch(buildApiUrl(`/applicants/me/job-posts/${encodeURIComponent(rawId)}/saved`), {
    method: "PUT",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
}

/** DELETE /applicants/me/job-posts/:jobPostId/saved */
export async function deleteApplicantJobPostSaved(jobPostId, authToken) {
  const t = String(authToken || "").trim();
  const rawId = String(jobPostId || "").trim();
  if (!t) throw new Error("Not signed in.");
  if (!rawId) throw new Error("Missing job post id.");
  const res = await fetch(buildApiUrl(`/applicants/me/job-posts/${encodeURIComponent(rawId)}/saved`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
}

/** GET /applicants/me/saved-job-posts/count — applicant auth; returns how many jobs are saved for later. */
export async function getApplicantSavedJobPostsCount(authToken) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const res = await fetch(buildApiUrl("/applicants/me/saved-job-posts/count"), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || body.data == null || typeof body.data.count !== "number") {
    throw new Error("Unexpected response from server");
  }
  return { count: body.data.count };
}

/**
 * GET /applicants/me/saved-job-posts?limit=&offset= — published jobs saved for later (newest first).
 * @returns {Promise<{ jobs: object[], total: number, hasMore: boolean }>}
 */
export async function getApplicantSavedJobPosts(authToken, { limit, offset } = {}) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const q = new URLSearchParams();
  if (limit != null && String(limit).trim() !== "") q.set("limit", String(limit));
  if (offset != null && String(offset).trim() !== "" && Number(offset) > 0) {
    q.set("offset", String(offset));
  }
  const qs = q.toString();
  const res = await fetch(buildApiUrl(`/applicants/me/saved-job-posts${qs ? `?${qs}` : ""}`), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body.data || typeof body.data !== "object") {
    throw new Error("Unexpected response from server");
  }
  const jobs = Array.isArray(body.data.jobs) ? body.data.jobs : [];
  const total = typeof body.data.total === "number" ? body.data.total : jobs.length;
  const off = Number(offset) || 0;
  const hasMore =
    typeof body.data.hasMore === "boolean" ? body.data.hasMore : off + jobs.length < total;
  return { jobs, total, hasMore };
}

/**
 * GET /applicants/me/applied-job-posts?limit=&offset= — published jobs the applicant has applied to (newest first).
 * Each job may include `appliedAt` (ISO string) when known.
 * @returns {Promise<{ jobs: object[], total: number, hasMore: boolean }>}
 */
export async function getApplicantAppliedJobPosts(authToken, { limit, offset } = {}) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const q = new URLSearchParams();
  if (limit != null && String(limit).trim() !== "") q.set("limit", String(limit));
  if (offset != null && String(offset).trim() !== "" && Number(offset) > 0) {
    q.set("offset", String(offset));
  }
  const qs = q.toString();
  const res = await fetch(buildApiUrl(`/applicants/me/applied-job-posts${qs ? `?${qs}` : ""}`), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body.data || typeof body.data !== "object") {
    throw new Error("Unexpected response from server");
  }
  const jobs = Array.isArray(body.data.jobs) ? body.data.jobs : [];
  const total = typeof body.data.total === "number" ? body.data.total : jobs.length;
  const off = Number(offset) || 0;
  const hasMore =
    typeof body.data.hasMore === "boolean" ? body.data.hasMore : off + jobs.length < total;
  return { jobs, total, hasMore };
}

/**
 * DELETE /applicants/me/job-posts/:jobPostId/application-pitch-video — clears stored application pitch.
 */
export async function deleteApplicantApplicationPitchVideo(jobPostId, authToken) {
  const t = String(authToken || "").trim();
  const rawId = String(jobPostId || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  if (!rawId) {
    throw new Error("Missing job post id.");
  }
  const res = await fetch(
    buildApiUrl(`/applicants/me/job-posts/${encodeURIComponent(rawId)}/application-pitch-video`),
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${t}`,
      },
    }
  );
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.application) {
    throw new Error("Unexpected response from server");
  }
  return { application: body.data.application, pitchVideoUrl: body.data.pitchVideoUrl || "" };
}

/**
 * DELETE /hiring/me/job-posts/:id/pitch-video — deletes stored pitch and clears DB field.
 */
export async function deleteHiringJobPitchVideo(jobPostId, authToken) {
  const t = String(authToken || "").trim();
  const rawId = String(jobPostId || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  if (!rawId) {
    throw new Error("Missing job post id.");
  }
  const res = await fetch(buildApiUrl(`/hiring/me/job-posts/${encodeURIComponent(rawId)}/pitch-video`), {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${t}`,
    },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.jobPost) {
    throw new Error("Unexpected response from server");
  }
  return { jobPost: body.data.jobPost, pitchVideoUrl: body.data.pitchVideoUrl || "" };
}

/**
 * GET /hiring/me/job-posts/:id — fetch a hiring-owned job post.
 */
export async function getHiringJobPost(id, authToken) {
  const t = String(authToken || "").trim();
  const rawId = String(id || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  if (!rawId) {
    throw new Error("Missing job post id.");
  }
  const res = await fetch(buildApiUrl(`/hiring/me/job-posts/${encodeURIComponent(rawId)}`), {
    headers: {
      Authorization: `Bearer ${t}`,
    },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.jobPost) {
    throw new Error("Unexpected response from server");
  }
  return { jobPost: body.data.jobPost };
}

/**
 * DELETE /hiring/me/job-posts/:id — permanently deletes the row when status is `draft` only.
 */
export async function deleteHiringJobPostDraft(jobPostId, authToken) {
  const t = String(authToken || "").trim();
  const rawId = String(jobPostId || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  if (!rawId) {
    throw new Error("Missing job post id.");
  }
  const res = await fetch(buildApiUrl(`/hiring/me/job-posts/${encodeURIComponent(rawId)}`), {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${t}`,
    },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || body?.data?.deletedId == null) {
    throw new Error("Unexpected response from server");
  }
  return { deletedId: String(body.data.deletedId) };
}

/**
 * GET /hiring/me/job-posts — list current hiring user's job posts.
 */
export async function listHiringJobPosts(params, authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const search = new URLSearchParams();
  if (params && typeof params === "object") {
    if (params.status) search.set("status", String(params.status));
    if (params.limit != null) search.set("limit", String(params.limit));
    if (params.cursor) search.set("cursor", String(params.cursor));
  }
  const q = search.toString();
  const path = q ? `/hiring/me/job-posts?${q}` : "/hiring/me/job-posts";
  const res = await fetch(buildApiUrl(path), {
    headers: {
      Authorization: `Bearer ${t}`,
    },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !Array.isArray(body?.data?.jobPosts)) {
    throw new Error("Unexpected response from server");
  }
  return { jobPosts: body.data.jobPosts, page: body.data.page || null };
}

/**
 * GET /hiring/me/job-posts/status-counts — account-wide counts by dashboard status (active/hidden/closed/draft).
 */
export async function getHiringJobPostStatusCounts(authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/hiring/me/job-posts/status-counts"), {
    headers: {
      Authorization: `Bearer ${t}`,
    },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.statusCounts || typeof body.data.statusCounts !== "object") {
    throw new Error("Unexpected response from server");
  }
  const c = body.data.statusCounts;
  return {
    active: Math.max(0, Math.floor(Number(c.active) || 0)),
    hidden: Math.max(0, Math.floor(Number(c.hidden) || 0)),
    closed: Math.max(0, Math.floor(Number(c.closed) || 0)),
    draft: Math.max(0, Math.floor(Number(c.draft) || 0)),
  };
}

const DEFAULT_APPLICANT_STATS = Object.freeze({
  totalApplicants: 0,
  newThisWeek: 0,
  totalNewApplicants: 0,
});

/**
 * GET /hiring/me/applicant-stats — account-wide applicant totals (applied_at, UTC windows).
 */
export async function getHiringApplicantStats(authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/hiring/me/applicant-stats"), {
    headers: {
      Authorization: `Bearer ${t}`,
    },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.applicantStats || typeof body.data.applicantStats !== "object") {
    throw new Error("Unexpected response from server");
  }
  const s = body.data.applicantStats;
  return {
    totalApplicants: Math.max(0, Math.floor(Number(s.totalApplicants) || 0)),
    newThisWeek: Math.max(0, Math.floor(Number(s.newThisWeek) || 0)),
    totalNewApplicants: Math.max(0, Math.floor(Number(s.totalNewApplicants) || 0)),
  };
}

export { DEFAULT_APPLICANT_STATS };

/**
 * GET /hiring/me/billing-summary — PAYG slots or subscription allowance for the signed-in HM.
 * Summary includes `defaultOperatingCountry`, `defaultOperatingCurrency`, `defaultOperatingCurrencySymbol`,
 * and `defaultAdvertisingCurrency` (null when unset). Pricing uses operating currency only.
 */
export async function getHiringBillingSummary(authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/hiring/me/billing-summary"), {
    headers: {
      Authorization: `Bearer ${t}`,
    },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.billingSummary || typeof body.data.billingSummary !== "object") {
    throw new Error("Unexpected response from server");
  }
  return body.data.billingSummary;
}

/**
 * GET /hiring/me/billing-transactions — slot purchase/use ledger for the signed-in HM.
 */
export async function getHiringBillingTransactions(authToken, { limit = 50, offset = 0 } = {}) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const params = new URLSearchParams();
  if (limit != null) params.set("limit", String(limit));
  if (offset != null) params.set("offset", String(offset));
  const qs = params.toString();
  const res = await fetch(buildApiUrl(`/hiring/me/billing-transactions${qs ? `?${qs}` : ""}`), {
    headers: {
      Authorization: `Bearer ${t}`,
    },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !Array.isArray(body?.data?.transactions)) {
    throw new Error("Unexpected response from server");
  }
  return { transactions: body.data.transactions };
}

/**
 * POST /applicants/me/profile-photo — multipart field `photo` (JPEG/PNG/WebP; max size from API `PROFILE_PHOTO_MAX_MB`, default 10MB).
 */
export async function postApplicantProfilePhoto(file, authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  if (!file || typeof file !== "object") {
    throw new Error("Choose an image file first.");
  }
  const fd = new FormData();
  fd.append("photo", file);
  const res = await fetch(buildApiUrl("/applicants/me/profile-photo"), {
    method: "POST",
    headers: { Authorization: `Bearer ${t}` },
    body: fd,
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Upload failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * POST /hiring/me/profile-photo — hiring-manager profile photo (multipart field `photo`).
 */
export async function postHiringProfilePhoto(file, authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  if (!file || typeof file !== "object") {
    throw new Error("Choose an image file first.");
  }
  const fd = new FormData();
  fd.append("photo", file);
  const res = await fetch(buildApiUrl("/hiring/me/profile-photo"), {
    method: "POST",
    headers: { Authorization: `Bearer ${t}` },
    body: fd,
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Upload failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * DELETE /hiring/me/profile-photo — clears stored hiring profile photo.
 */
export async function deleteHiringProfilePhoto(authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/hiring/me/profile-photo"), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * POST /hiring/me/company-logo — hiring-manager company logo (multipart field `logo`).
 */
export async function postHiringCompanyLogo(file, authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  if (!file || typeof file !== "object") {
    throw new Error("Choose an image file first.");
  }
  const fd = new FormData();
  fd.append("logo", file);
  const res = await fetch(buildApiUrl("/hiring/me/company-logo"), {
    method: "POST",
    headers: { Authorization: `Bearer ${t}` },
    body: fd,
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Upload failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * DELETE /hiring/me/company-logo — clears stored hiring company logo.
 */
export async function deleteHiringCompanyLogo(authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/hiring/me/company-logo"), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * DELETE /applicants/me/profile-photo — clears stored applicant profile photo.
 */
export async function deleteApplicantProfilePhoto(authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/applicants/me/profile-photo"), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * POST /applicants/me/cv — multipart field `cv` (PDF, DOC, or DOCX; max size same as profile photo limit).
 */
export async function postApplicantCv(file, authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  if (!file || typeof file !== "object") {
    throw new Error("Choose a CV file first.");
  }
  const fd = new FormData();
  fd.append("cv", file);
  const res = await fetch(buildApiUrl("/applicants/me/cv"), {
    method: "POST",
    headers: { Authorization: `Bearer ${t}` },
    body: fd,
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Upload failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * DELETE /applicants/me/cv — clears stored applicant CV (file + DB).
 */
export async function deleteApplicantCv(authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const res = await fetch(buildApiUrl("/applicants/me/cv"), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * POST /applicants/me/work-country-evidence — multipart field `evidence` plus `rowId` (same id as in `workCountries` PATCH).
 * Allowed types: PDF or image PNG / JPEG / WebP (server-enforced).
 */
export async function postApplicantWorkEvidence(file, rowId, authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  if (!file || typeof file !== "object") {
    throw new Error("Choose a proof file first.");
  }
  const rid = String(rowId || "").trim();
  if (!rid) {
    throw new Error("Missing row id for proof upload.");
  }
  const fd = new FormData();
  fd.append("evidence", file);
  fd.append("rowId", rid);
  /** Query `rowId` as well: some proxies/CDNs mishandle multipart text fields; the API accepts both. */
  const path = `/applicants/me/work-country-evidence?rowId=${encodeURIComponent(rid)}`;
  const res = await fetch(buildApiUrl(path), {
    method: "POST",
    headers: { Authorization: `Bearer ${t}` },
    body: fd,
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Upload failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

/**
 * DELETE /applicants/me/work-country-evidence/:rowId — clears proof for that row (optional; clearing via PATCH `clearProof` is preferred).
 */
/**
 * GET /applicants/me/work-country-evidence/:rowId — fetch saved proof as a Blob (e.g. for download in the browser).
 */
export async function fetchApplicantWorkEvidenceBlob(rowId, authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const raw = String(rowId || "").trim();
  if (!raw) {
    throw new Error("Missing row id.");
  }
  const res = await fetch(buildApiUrl(`/applicants/me/work-country-evidence/${encodeURIComponent(raw)}`), {
    method: "GET",
    headers: { Authorization: `Bearer ${t}` },
  });
  if (!res.ok) {
    const body = await readApiBody(res);
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  return res.blob();
}

export async function deleteApplicantWorkEvidence(rowId, authToken) {
  const t = String(authToken || "").trim();
  if (!t) {
    throw new Error("Not signed in.");
  }
  const raw = String(rowId || "").trim();
  if (!raw) {
    throw new Error("Missing row id.");
  }
  const res = await fetch(buildApiUrl(`/applicants/me/work-country-evidence/${encodeURIComponent(raw)}`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.user) {
    throw new Error("Unexpected response from server");
  }
  return { user: body.data.user };
}

export async function listApplicantSavedSearches(authToken) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const res = await fetch(buildApiUrl("/applicants/me/saved-searches"), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !Array.isArray(body?.data?.savedSearches)) {
    throw new Error("Unexpected response from server");
  }
  return body.data.savedSearches;
}

export async function postApplicantSavedSearch({ name, criteria } = {}, authToken) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const res = await fetch(buildApiUrl("/applicants/me/saved-searches"), {
    method: "POST",
    headers: { Authorization: `Bearer ${t}`, "Content-Type": "application/json" },
    body: JSON.stringify({
      name: String(name || "").trim(),
      criteria: criteria && typeof criteria === "object" ? criteria : {},
    }),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.savedSearch) {
    throw new Error("Unexpected response from server");
  }
  return body.data.savedSearch;
}

/**
 * POST /applicants/me/job-search — cursor-paginated published job_posts search (applicant auth).
 * Body: { countryCodes, keywords, salaryCurrency, salaryMin, salaryMax, workMode?, cursor?, limit? }
 */
export async function postApplicantJobSearch(payload = {}, authToken) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const res = await fetch(buildApiUrl("/applicants/me/job-search"), {
    method: "POST",
    headers: { Authorization: `Bearer ${t}`, "Content-Type": "application/json" },
    body: JSON.stringify(payload && typeof payload === "object" ? payload : {}),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !Array.isArray(body?.data?.jobs) || !body?.data?.page) {
    throw new Error("Unexpected response from server");
  }
  return { jobs: body.data.jobs, page: body.data.page };
}

/**
 * POST /public/job-search — same search as applicant job search, no auth (shareable links).
 */
export async function postPublicJobSearch(payload = {}) {
  const res = await fetch(buildApiUrl("/public/job-search"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload && typeof payload === "object" ? payload : {}),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !Array.isArray(body?.data?.jobs) || !body?.data?.page) {
    throw new Error("Unexpected response from server");
  }
  return { jobs: body.data.jobs, page: body.data.page };
}

/**
 * GET /applicants/me/job-search/latest-preview — up to 5 jobs from re-running the last successful search (applicant auth).
 * @returns {Promise<{ hasLastSearch: boolean, lastSearchAt: string | null, jobs: object[], replayFailed?: boolean }>}
 */
export async function getApplicantJobSearchLatestPreview(authToken) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const res = await fetch(buildApiUrl("/applicants/me/job-search/latest-preview"), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body.data || typeof body.data !== "object") {
    throw new Error("Unexpected response from server");
  }
  const d = body.data;
  return {
    hasLastSearch: Boolean(d.hasLastSearch),
    lastSearchAt: d.lastSearchAt != null ? String(d.lastSearchAt) : null,
    jobs: Array.isArray(d.jobs) ? d.jobs : [],
    replayFailed: Boolean(d.replayFailed),
  };
}

/**
 * GET /applicants/me/job-search/last-criteria — stored criteria from last successful search (applicant auth).
 * @returns {Promise<{ hasLastSearch: boolean, lastSearchAt: string | null, criteria: object | null }>}
 */
export async function getApplicantJobSearchLastCriteria(authToken) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const res = await fetch(buildApiUrl("/applicants/me/job-search/last-criteria"), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body.data || typeof body.data !== "object") {
    throw new Error("Unexpected response from server");
  }
  const d = body.data;
  return {
    hasLastSearch: Boolean(d.hasLastSearch),
    lastSearchAt: d.lastSearchAt != null ? String(d.lastSearchAt) : null,
    criteria: d.criteria && typeof d.criteria === "object" ? d.criteria : null,
  };
}

/**
 * GET /applicants/me/published-job-posts/:id — published job post by UUID (applicant auth).
 */
export async function getApplicantPublishedJobPost(jobPostId, authToken) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const rawId = String(jobPostId || "").trim();
  if (!rawId) throw new Error("Missing job post id.");
  const res = await fetch(
    buildApiUrl(`/applicants/me/published-job-posts/${encodeURIComponent(rawId)}`),
    {
      headers: { Authorization: `Bearer ${t}` },
    }
  );
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.jobPost) {
    throw new Error("Unexpected response from server");
  }
  return { jobPost: body.data.jobPost };
}

/**
 * GET /public/pricing?countryCode=GB — hiring pricing for a country (no auth).
 */
export async function getPublicPricing(countryCode) {
  const code = String(countryCode || "").trim().toUpperCase();
  const qs = code ? `?${new URLSearchParams({ countryCode: code })}` : "";
  const res = await fetch(buildApiUrl(`/public/pricing${qs}`), {
    headers: { Accept: "application/json" },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data;
}

/**
 * GET /public/job-posts/:slugOrId — published listed job (no auth; crawler-friendly).
 */
export async function getPublicPublishedJobPost(slugOrId) {
  const raw = String(slugOrId || "").trim();
  if (!raw) throw new Error("Missing job reference.");
  const res = await fetch(buildApiUrl(`/public/job-posts/${encodeURIComponent(raw)}`), {
    headers: { Accept: "application/json" },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body?.data?.jobPost) {
    throw new Error("Unexpected response from server");
  }
  return { jobPost: body.data.jobPost };
}

/**
 * GET /public/job-posts/:slugOrId/comments — nested comments (no auth).
 * @returns {Promise<{ comments: object[] }>}
 */
export async function listPublicJobPostComments(slugOrId) {
  const raw = String(slugOrId || "").trim();
  if (!raw) throw new Error("Missing job reference.");
  const res = await fetch(buildApiUrl(`/public/job-posts/${encodeURIComponent(raw)}/comments`), {
    headers: { Accept: "application/json" },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body.data || typeof body.data !== "object") {
    throw new Error("Unexpected response from server");
  }
  const comments = Array.isArray(body.data.comments) ? body.data.comments : [];
  return { comments };
}

/**
 * GET /job-posts/:jobPostId/comments — nested comments (applicant or hiring auth).
 * @returns {Promise<{ comments: object[] }>}
 */
export async function listJobPostComments(jobPostId, authToken) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const rawId = String(jobPostId || "").trim();
  if (!rawId) throw new Error("Missing job post id.");
  const res = await fetch(buildApiUrl(`/job-posts/${encodeURIComponent(rawId)}/comments`), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok || !body.data || typeof body.data !== "object") {
    throw new Error("Unexpected response from server");
  }
  const comments = Array.isArray(body.data.comments) ? body.data.comments : [];
  return { comments };
}

/**
 * POST /job-posts/:jobPostId/comments — body: { body, parentId? }.
 * @returns {Promise<{ comment: object }>}
 */
export async function postJobPostComment(jobPostId, authToken, { body, parentId } = {}) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const rawId = String(jobPostId || "").trim();
  if (!rawId) throw new Error("Missing job post id.");
  const payload = { body: String(body || "").trim() };
  if (parentId != null && String(parentId).trim() !== "") {
    payload.parentId = String(parentId).trim();
  }
  const res = await fetch(buildApiUrl(`/job-posts/${encodeURIComponent(rawId)}/comments`), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const resBody = await readApiBody(res);
  if (!res.ok) {
    const message = resBody?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = resBody?.error?.code;
    throw err;
  }
  if (!resBody?.ok || !resBody.data?.comment) {
    throw new Error("Unexpected response from server");
  }
  return { comment: resBody.data.comment };
}

/** DELETE /job-posts/comments/:commentId — author only. */
export async function deleteJobPostComment(commentId, authToken) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const id = String(commentId || "").trim();
  if (!id) throw new Error("Missing comment id.");
  const res = await fetch(buildApiUrl(`/job-posts/comments/${encodeURIComponent(id)}`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok) {
    throw new Error("Unexpected response from server");
  }
  return body.data || {};
}

export async function deleteApplicantSavedSearch(savedSearchId, authToken) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const id = String(savedSearchId || "").trim();
  if (!id) throw new Error("Missing saved search id.");
  const res = await fetch(buildApiUrl(`/applicants/me/saved-searches/${encodeURIComponent(id)}`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    const err = new Error(message);
    err.code = body?.error?.code;
    throw err;
  }
  if (!body?.ok) {
    throw new Error("Unexpected response from server");
  }
  return body.data || {};
}

function notificationsApiBase(role) {
  const r = String(role || "").trim();
  if (r === "hiring-manager" || r === "recruiter") {
    return "/hiring/me/notifications";
  }
  return "/applicants/me/notifications";
}

/** GET …/notifications/active-count — open notifications for bell badge. */
export async function getUserNotificationsActiveCount(authToken, role) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const res = await fetch(buildApiUrl(`${notificationsApiBase(role)}/active-count`), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data || { count: 0 };
}

/** GET …/notifications — status: active | history | all */
export async function getUserNotifications(authToken, role, { status = "active", limit = 20, offset = 0 } = {}) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const params = new URLSearchParams({
    status: String(status || "active"),
    limit: String(limit),
    offset: String(offset),
  });
  const res = await fetch(buildApiUrl(`${notificationsApiBase(role)}?${params}`), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data || { notifications: [], total: 0, limit, offset, status };
}

/** PATCH …/notifications/:id/dismiss */
export async function dismissUserNotification(authToken, role, notificationId) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const id = String(notificationId || "").trim();
  if (!id) throw new Error("Missing notification id.");
  const res = await fetch(buildApiUrl(`${notificationsApiBase(role)}/${encodeURIComponent(id)}/dismiss`), {
    method: "PATCH",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data || {};
}

/** GET /hiring/me/response-settings — signature + rejection templates for the signed-in hiring user. */
export async function getHiringResponseSettings(authToken) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const res = await fetch(buildApiUrl("/hiring/me/response-settings"), {
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data || { signature: "", rejectionTemplates: [] };
}

/** PUT /hiring/me/response-settings/signature */
export async function putHiringResponseSignature(authToken, signature) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const res = await fetch(buildApiUrl("/hiring/me/response-settings/signature"), {
    method: "PUT",
    headers: { Authorization: `Bearer ${t}`, "Content-Type": "application/json" },
    body: JSON.stringify({ signature: String(signature ?? "") }),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data || {};
}

/** PUT /hiring/me/response-settings/rejection-templates/:templateKey */
export async function putHiringRejectionTemplate(authToken, templateKey, { label, responseHtml } = {}) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const key = String(templateKey || "").trim();
  if (!key) throw new Error("Missing template key.");
  const res = await fetch(buildApiUrl(`/hiring/me/response-settings/rejection-templates/${encodeURIComponent(key)}`), {
    method: "PUT",
    headers: { Authorization: `Bearer ${t}`, "Content-Type": "application/json" },
    body: JSON.stringify({
      label: label != null ? String(label) : undefined,
      responseHtml: responseHtml != null ? String(responseHtml) : undefined,
    }),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data?.template || body.data || {};
}

/** POST /hiring/me/response-settings/rejection-templates */
export async function postHiringRejectionTemplate(authToken, { label, responseHtml } = {}) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const res = await fetch(buildApiUrl("/hiring/me/response-settings/rejection-templates"), {
    method: "POST",
    headers: { Authorization: `Bearer ${t}`, "Content-Type": "application/json" },
    body: JSON.stringify({
      label: String(label || "").trim(),
      responseHtml: String(responseHtml ?? ""),
    }),
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data?.template || body.data || {};
}

/** DELETE /hiring/me/response-settings/rejection-templates/:templateKey */
export async function deleteHiringRejectionTemplate(authToken, templateKey) {
  const t = String(authToken || "").trim();
  if (!t) throw new Error("Not signed in.");
  const key = String(templateKey || "").trim();
  if (!key) throw new Error("Missing template key.");
  const res = await fetch(buildApiUrl(`/hiring/me/response-settings/rejection-templates/${encodeURIComponent(key)}`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${t}` },
  });
  const body = await readApiBody(res);
  if (!res.ok) {
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  if (!body?.ok) throw new Error("Unexpected response from server");
  return body.data || {};
}
